import type { OfflineTripSnapshot } from './types';
import { clearTripEditorDrafts } from '@/lib/trip-planning/account-draft-storage';

const DB_NAME = 'roam-offline';
const DB_VERSION = 1;
const SNAPSHOT_STORE = 'trip-snapshots';
const CHAT_DRAFT_PREFIX = 'roam:chat-draft:';
const PRIVATE_CACHE_PREFIX = 'roam-private-';

export async function saveOfflineTripSnapshot(snapshot: OfflineTripSnapshot): Promise<void> {
  const db = await openOfflineDb();
  const current = await getFromStore<OfflineTripSnapshot>(db, SNAPSHOT_STORE, snapshot.tripId);
  const next: OfflineTripSnapshot = {
    ...(current ?? snapshot),
    ...snapshot,
    itineraryDays: snapshot.itineraryDays ?? current?.itineraryDays,
    dashboard: snapshot.dashboard ?? current?.dashboard,
    sleep: snapshot.sleep ?? current?.sleep,
    meta: snapshot.meta,
  };
  await putInStore(db, SNAPSHOT_STORE, next, snapshot.tripId);
  db.close();
}

export async function getOfflineTripSnapshot(tripId: string): Promise<OfflineTripSnapshot | null> {
  const db = await openOfflineDb();
  const snapshot = await getFromStore<OfflineTripSnapshot>(db, SNAPSHOT_STORE, tripId);
  db.close();
  return snapshot ?? null;
}

export function saveChatDraft(tripId: string, value: string): void {
  window.localStorage.setItem(`${CHAT_DRAFT_PREFIX}${tripId}`, value);
}

export function loadChatDraft(tripId: string): string {
  return window.localStorage.getItem(`${CHAT_DRAFT_PREFIX}${tripId}`) ?? '';
}

export function clearChatDraft(tripId: string): void {
  window.localStorage.removeItem(`${CHAT_DRAFT_PREFIX}${tripId}`);
}

export async function clearPrivateOfflineData(): Promise<void> {
  // Clear tab drafts synchronously, even if IndexedDB is blocked or fails.
  clearTripEditorDrafts();
  let deletionError: unknown = null;
  try {
    await new Promise<void>((resolve, reject) => {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error ?? new Error('Offline database deletion failed.'));
      req.onblocked = () =>
        reject(new Error('Offline database deletion was blocked by another open Roam tab.'));
    });
  } catch (error) {
    // Continue clearing drafts and Cache Storage even if IndexedDB is blocked;
    // callers still receive the failure so it cannot be reported as success.
    deletionError = error;
  }

  for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(CHAT_DRAFT_PREFIX)) window.localStorage.removeItem(key);
  }

  if ('caches' in window) {
    const names = await window.caches.keys();
    await Promise.all(
      names
        .filter((name) => name.startsWith(PRIVATE_CACHE_PREFIX))
        .map((name) => caches.delete(name)),
    );
  }

  if (deletionError) throw deletionError;
}

function openOfflineDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) db.createObjectStore(SNAPSHOT_STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      // Another tab may request account-data deletion after sign-out. Closing
      // on version change prevents this connection from blocking that wipe.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}

function getFromStore<T>(db: IDBDatabase, storeName: string, key: string): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

function putInStore<T>(db: IDBDatabase, storeName: string, value: T, key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
